/**
 * DeepL front-end translation.
 * Requires: [data-deepl-translate] (select), [data-toggle-translate-menu], [data-deepl-reset], optional .translate-wrapper. Skips #wpadminbar and .translate-menu.
 * Localized: strl_vars.ajax_url, strl_vars.stylesheet_directory, strl_vars.algolia_deepl_placeholder (optional). Body copy is translated per text node under leaf elements so inline tags (e.g. strong, sr-only) stay in place. Text, input placeholders and image alts are sent in one admin-ajax request per batch (unless total strings exceed batch size).
 * Body: data-post-id (singular pages).
 * Session: deepl-language (target), deepl-source-lang (CMS page language, set once per tab).
 */
export default class DeepL {
	constructor() {
		this.baseUrl = 'https://api.deepl.com/v2';
		this.languageNames = new Intl.DisplayNames(['en'], {
			type: 'language',
		});
	}

	persistDeeplSourceLang() {
		if (sessionStorage.getItem('deepl-source-lang')) {
			return;
		}
		const raw = document.documentElement.getAttribute('lang') || '';
		const code = raw.split('-')[0];
		if (code) {
			sessionStorage.setItem('deepl-source-lang', code);
		}
	}

	getDeeplSourceLang() {
		const stored = sessionStorage.getItem('deepl-source-lang');
		if (stored) {
			return stored;
		}
		const raw = document.documentElement.lang || '';
		return raw.split('-')[0] || '';
	}

	revertToOriginalLanguage() {
		sessionStorage.removeItem('deepl-language');
		window.location.reload();
	}

	async init() {
		const languageMenuToggle = document.querySelectorAll('[data-toggle-translate-menu]');
		const languageSelector = document.querySelectorAll('[data-deepl-translate]');
		const languageReset = document.querySelectorAll('[data-deepl-reset]');

		if (!languageSelector.length && !languageMenuToggle.length) {
			return;
		}

		this.persistDeeplSourceLang();

		const storedLanguage = sessionStorage.getItem('deepl-language');
		const originalLang = this.getDeeplSourceLang();
		if (storedLanguage && originalLang && storedLanguage.toUpperCase() === originalLang.toUpperCase()) {
			this.revertToOriginalLanguage();
			return;
		}

		if (storedLanguage) {
			const sourceLang = document.documentElement.lang.split('-')[0];
			this.updateSelector(storedLanguage);
			this.initTranslation(storedLanguage, sourceLang);
		}

		languageMenuToggle.forEach((toggle) => {
			const menu = toggle.closest('.translate-menu');
			const languageMenu = menu ? menu.querySelector('[data-translate-menu]') : null;
			toggle.addEventListener('click', () => {
				if (languageMenu) {
					languageMenu.classList.toggle('hidden');
					languageMenu.ariaExpanded = languageMenu.ariaExpanded === 'false' ? 'true' : 'false';
				}
			});

			document.addEventListener('keyup', (e) => {
				if (languageMenu && e.key === 'Escape' && !languageMenu.classList.contains('hidden')) {
					languageMenu.classList.toggle('hidden');
					languageMenu.ariaExpanded = languageMenu.ariaExpanded === 'false' ? 'true' : 'false';
				}
			});

			document.addEventListener('click', (event) => {
				if (!languageMenu || languageMenu.classList.contains('hidden')) {
					return;
				}

				const clickTarget = event.target;
				if (!(clickTarget instanceof Element)) {
					return;
				}

				if (!menu || menu.contains(clickTarget)) {
					return;
				}

				languageMenu.classList.add('hidden');
				languageMenu.ariaExpanded = 'false';
			});
		});

		languageSelector.forEach((selector) => {
			selector.addEventListener('change', () => {
				const selectedLanguage = selector.value;
				if (!selectedLanguage) {
					return;
				}

				const selectedOption = selector.options[selector.selectedIndex];
				const selectedLanguageName = selectedOption ? selectedOption.textContent.trim() : '';

				this.selectLanguage(selectedLanguage, selectedLanguageName);
			});
		});

		languageReset.forEach((resetbtn) => {
			resetbtn.addEventListener('click', () => {
				sessionStorage.removeItem('deepl-language');
				window.location.reload();
			});
		});
	}

	async selectLanguage(selector, selectedLanguageName = '') {
		this.persistDeeplSourceLang();

		const sourceLang = document.documentElement.lang.split('-')[0];
		const selectedLanguage =
			typeof selector === 'string' ? selector : selector && selector.value ? selector.value : '';
		if (!selectedLanguage) {
			return;
		}

		const originalLang = this.getDeeplSourceLang();
		if (originalLang && selectedLanguage.toUpperCase() === originalLang.toUpperCase()) {
			this.revertToOriginalLanguage();
			return;
		}

		this.updateCurrentLanguageDisplay(selectedLanguage, selectedLanguageName);
		sessionStorage.setItem('deepl-language', selectedLanguage);
		this.initTranslation(selectedLanguage, sourceLang);
	}

	updateSelectState(selector, targetLang) {
		if (!(selector instanceof HTMLSelectElement) || !targetLang) {
			return;
		}

		const normalized = targetLang.toUpperCase();
		const matchingOption = [...selector.options].find(
			(option) => option.value && option.value.toUpperCase() === normalized
		);

		if (matchingOption) {
			selector.value = matchingOption.value;
		}
	}

	updateCurrentLanguageDisplay(targetLang, targetName = '') {
		const currentLang = document.querySelectorAll('.current-lang');
		currentLang.forEach((span) => {
			const code = span.querySelector('.language-code');
			const nameEl = span.querySelector('.language-name');
			const sr = span.querySelector('.sr-only');
			if (code) code.textContent = targetLang.toUpperCase();
			const fallbackName = this.languageNames.of(targetLang) || targetLang.toUpperCase();
			const name = targetName || fallbackName;
			if (nameEl) nameEl.textContent = name;
			if (sr && !nameEl) sr.textContent = name;
		});
	}

	/**
	 * Collect #text nodes under a root in document order, skipping inert / excluded subtrees.
	 *
	 * @param {HTMLElement} root
	 * @return {Text[]}
	 */
	collectTranslatableTextNodes(root) {
		const out = [];
		const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
			acceptNode(node) {
				const parent = node.parentElement;
				if (!parent) {
					return NodeFilter.FILTER_REJECT;
				}
				if (parent.closest('script, style, noscript')) {
					return NodeFilter.FILTER_REJECT;
				}
				if (parent.closest('#wpadminbar')) {
					return NodeFilter.FILTER_REJECT;
				}
				if (parent.closest('.translate-menu')) {
					return NodeFilter.FILTER_REJECT;
				}
				if (parent.closest('.no-translate')) {
					return NodeFilter.FILTER_REJECT;
				}
				const value = node.nodeValue;
				if (value === null || value === '') {
					return NodeFilter.FILTER_REJECT;
				}
				if (!/[^\s\ufeff]/.test(value)) {
					return NodeFilter.FILTER_SKIP;
				}
				return NodeFilter.FILTER_ACCEPT;
			},
		});

		let next = walker.nextNode();
		while (next) {
			out.push(next);
			next = walker.nextNode();
		}
		return out;
	}

	/**
	 * How many text / input / image strings fall in global index range [start, end) after concatenating
	 * [all text, all placeholders, all alts].
	 *
	 * @param {number} start
	 * @param {number} end
	 * @param {number} textTotal
	 * @param {number} inputsTotal
	 * @return {{ text: number, inputs: number, images: number }}
	 */
	combinedSegmentCountsForSlice(start, end, textTotal, inputsTotal) {
		let text = 0;
		let inputs = 0;
		let images = 0;
		for (let i = start; i < end; i++) {
			if (i < textTotal) {
				text++;
			} else if (i < textTotal + inputsTotal) {
				inputs++;
			} else {
				images++;
			}
		}
		return { text, inputs, images };
	}

	/**
	 * One admin-ajax call: text nodes + placeholders + alts in a single DeepL request (PHP merges segments).
	 *
	 * @param {string[]} items Merged slice in order: text…, inputs…, images…
	 * @param {{ text: number, inputs: number, images: number }} segmentCounts Counts for this slice only
	 * @param {string} targetLang
	 * @param {string} sourceLang
	 * @return {Promise<{ stringsText: { translation?: string }[], stringsInputs: { translation?: string }[], stringsImages: { translation?: string }[] } | false>}
	 */
	async getTranslationCombined(items, segmentCounts, targetLang, sourceLang) {
		if (!window.strl_vars || !strl_vars.ajax_url) {
			return false;
		}

		const formData = new FormData();
		formData.append('action', 'get_deepl_translations');
		formData.append('target_lang', targetLang);
		formData.append('source_lang', sourceLang);
		formData.append('type', 'combined');
		formData.append('post_id', document.body.dataset.postId || 0);
		formData.append('combined_text_count', String(segmentCounts.text));
		formData.append('combined_inputs_count', String(segmentCounts.inputs));
		formData.append('combined_images_count', String(segmentCounts.images));

		items.forEach((item) => {
			formData.append('items[]', item);
		});

		const request = new Request(strl_vars.ajax_url, {
			cache: 'no-store',
			method: 'POST',
			body: formData,
		});

		try {
			const response = await fetch(request);
			const text = await response.text();
			const data = JSON.parse(text);
			if (!data.success) {
				console.warn('DeepL API error:', data.data || data);
				return false;
			}
			const payload = data.data || false;
			if (
				!payload ||
				!Array.isArray(payload.strings_text) ||
				!Array.isArray(payload.strings_inputs) ||
				!Array.isArray(payload.strings_images)
			) {
				return false;
			}
			if (
				payload.strings_text.length !== segmentCounts.text ||
				payload.strings_inputs.length !== segmentCounts.inputs ||
				payload.strings_images.length !== segmentCounts.images
			) {
				return false;
			}
			return {
				stringsText: payload.strings_text,
				stringsInputs: payload.strings_inputs,
				stringsImages: payload.strings_images,
			};
		} catch (err) {
			console.warn('DeepL:', err);
			return false;
		}
	}

	/**
	 * Batched combined requests when merged string count exceeds batchSize.
	 *
	 * @param {string[]} textStrings
	 * @param {string[]} placeholders
	 * @param {string[]} altTexts
	 * @param {string} targetLang
	 * @param {string} sourceLang
	 * @param {number} batchSize
	 * @return {Promise<{ stringsText: { translation?: string }[], stringsInputs: { translation?: string }[], stringsImages: { translation?: string }[] } | false>}
	 */
	async getTranslationCombinedBatched(textStrings, placeholders, altTexts, targetLang, sourceLang, batchSize = 500) {
		const merged = textStrings.concat(placeholders, altTexts);
		const textTotal = textStrings.length;
		const inputsTotal = placeholders.length;

		if (merged.length === 0) {
			return {
				stringsText: [],
				stringsInputs: [],
				stringsImages: [],
			};
		}

		const accText = [];
		const accInputs = [];
		const accImages = [];

		for (let start = 0; start < merged.length; start += batchSize) {
			const end = Math.min(start + batchSize, merged.length);
			const slice = merged.slice(start, end);
			const counts = this.combinedSegmentCountsForSlice(start, end, textTotal, inputsTotal);
			const data = await this.getTranslationCombined(slice, counts, targetLang, sourceLang);
			if (!data) {
				return false;
			}
			for (let i = 0; i < data.stringsText.length; i++) {
				accText.push(data.stringsText[i]);
			}
			for (let j = 0; j < data.stringsInputs.length; j++) {
				accInputs.push(data.stringsInputs[j]);
			}
			for (let k = 0; k < data.stringsImages.length; k++) {
				accImages.push(data.stringsImages[k]);
			}
		}

		return {
			stringsText: accText,
			stringsInputs: accInputs,
			stringsImages: accImages,
		};
	}

	showDeeplTranslatingOverlay() {
		if (document.querySelector('[data-deepl-translate-overlay]')) {
			return;
		}

		const overlay = document.createElement('div');
		overlay.setAttribute('data-deepl-translate-overlay', '');
		overlay.className =
			'deepl-translate-overlay fixed inset-0 z-[2147483646] flex items-center justify-center bg-black/40';
		overlay.setAttribute('role', 'presentation');

		const inner = document.createElement('div');
		inner.className = 'deepl-translate-overlay-inner flex flex-col items-center gap-4';
		inner.setAttribute('role', 'status');
		inner.setAttribute('aria-live', 'polite');

		const spinner = document.createElement('div');
		spinner.className = 'size-12 rounded-full border-4 border-solid border-white/30 border-t-white animate-spin';
		spinner.setAttribute('aria-hidden', 'true');

		const message = document.createElement('p');
		message.className = 'text-center text-base font-medium text-white';
		message.textContent = 'translating...';

		inner.appendChild(spinner);
		inner.appendChild(message);
		overlay.appendChild(inner);
		document.body.appendChild(overlay);
		document.body.dataset.deeplOverlayScrollLock = document.body.style.overflow || '';
		document.body.style.overflow = 'hidden';
	}

	hideDeeplTranslatingOverlay() {
		const overlay = document.querySelector('[data-deepl-translate-overlay]');
		if (overlay && overlay.parentNode) {
			overlay.parentNode.removeChild(overlay);
		}
		if (document.body.dataset.deeplOverlayScrollLock !== undefined) {
			document.body.style.overflow = document.body.dataset.deeplOverlayScrollLock;
			delete document.body.dataset.deeplOverlayScrollLock;
		}
	}

	async initTranslation(targetLang, sourceLang, container = document) {
		const sourceUpper = (sourceLang || document.documentElement.lang.split('-')[0]).toUpperCase();
		const targetUpper = (targetLang || '').toUpperCase();
		if (sourceUpper === targetUpper) {
			return;
		}

		const rsBtn = document.querySelectorAll('.rsbtn');
		rsBtn.forEach((btn) => btn.classList.add('opacity-50', 'pointer-events-none'));
		if (rsBtn.length && targetLang !== 'EN' && targetLang !== 'NL') {
			rsBtn.forEach((btn) => (btn.style.display = 'none'));
		} else if (rsBtn.length) {
			rsBtn.forEach((btn) => {
				const play = btn.querySelector('.rsbtn_play');
				if (play && play.href) {
					play.href = targetLang === 'EN' ? play.href.replace('lang=nl_nl', 'lang=en') : play.href;
				}
				btn.style.display = 'block';
			});
			if (window.ReadSpeaker) {
				window.ReadSpeaker.init();
			}
		}

		let translateWrapper;
		if (container !== document) {
			translateWrapper = container;
		} else {
			translateWrapper = container.querySelector('.translate-wrapper');
			// Use body so header, footer and Gravity Forms (anywhere on page) are translated
			if (!translateWrapper) {
				translateWrapper = document.body;
			}
		}

		if (!translateWrapper) {
			rsBtn.forEach((btn) => btn.classList.remove('opacity-50', 'pointer-events-none'));
			return;
		}

		const candidateElements = translateWrapper.querySelectorAll(
			'h1, h2, h3, h4, h5, h6, p, span, li:not(.menu-item), a, button, summary, small, label, blockquote, th, td, option'
		);

		const candidateList = [...candidateElements];
		const textStrings = [];
		const replaceTextNodes = [];
		candidateList.forEach((node) => {
			if (
				!candidateList.includes(node.parentElement) &&
				node.textContent &&
				!node.classList.contains('no-translate') &&
				!node.closest('.translate-menu') &&
				!node.closest('#wpadminbar')
			) {
				const collected = this.collectTranslatableTextNodes(node);
				collected.forEach((textNode) => {
					replaceTextNodes.push(textNode);
					textStrings.push(textNode.nodeValue || '');
				});
			}
		});

		const inputs = translateWrapper.querySelectorAll('input[placeholder]');
		const placeholders = [];
		const inputsToTranslate = [];
		inputs.forEach((input) => {
			if (input.closest('.translate-menu') || input.closest('#wpadminbar')) {
				return;
			}

			let placeholder = '';
			if (
				input.classList.contains('ais-SearchBox-input') &&
				typeof strl_vars !== 'undefined' &&
				strl_vars.algolia_deepl_placeholder
			) {
				placeholder = strl_vars.algolia_deepl_placeholder;
			} else {
				placeholder = input.getAttribute('placeholder') || '';
			}

			if (!placeholder.trim()) {
				return;
			}

			inputsToTranslate.push(input);
			placeholders.push(placeholder);
		});

		const images = translateWrapper.querySelectorAll('img[alt]');
		const altTexts = [];
		const imagesForAlt = [];
		images.forEach((image) => {
			if (image.closest('.translate-menu') || image.closest('#wpadminbar')) {
				return;
			}

			const alt = image.getAttribute('alt') || '';
			if (!alt.trim()) {
				return;
			}

			altTexts.push(alt);
			imagesForAlt.push(image);
		});

		let translationApplied = false;
		const needsOverlay = textStrings.length > 0 || placeholders.length > 0 || altTexts.length > 0;

		if (needsOverlay) {
			this.showDeeplTranslatingOverlay();
		}

		try {
			const hasAnyStrings = textStrings.length > 0 || placeholders.length > 0 || altTexts.length > 0;
			if (hasAnyStrings) {
				const translations = await this.getTranslationCombinedBatched(
					textStrings,
					placeholders,
					altTexts,
					targetLang,
					sourceLang
				);

				if (!translations) {
					console.warn(
						'DeepL: Geen vertaling ontvangen. Controleer de API-key in Stuurlui Global → DeepL en de browserconsole (Network-tab) voor de AJAX-response.'
					);
				} else {
					if (translations.stringsText.length === replaceTextNodes.length) {
						if (replaceTextNodes.length > 0) {
							translationApplied = true;
						}
						replaceTextNodes.forEach((node, index) => {
							if (translations.stringsText[index] && translations.stringsText[index].translation) {
								const raw = translations.stringsText[index].translation.split('\\').join('');
								node.nodeValue = raw;
							}
						});
					}

					if (translations.stringsInputs.length === inputsToTranslate.length) {
						inputsToTranslate.forEach((input, index) => {
							if (translations.stringsInputs[index] && translations.stringsInputs[index].translation) {
								input.setAttribute('placeholder', translations.stringsInputs[index].translation);
								if (input.hasAttribute('aria-label') && input.hasAttribute('placeholder')) {
									input.setAttribute('aria-label', translations.stringsInputs[index].translation);
								}
							}
						});
					}

					if (translations.stringsImages.length === imagesForAlt.length) {
						imagesForAlt.forEach((image, index) => {
							if (translations.stringsImages[index] && translations.stringsImages[index].translation) {
								image.setAttribute('alt', translations.stringsImages[index].translation);
							}
						});
					}
				}
			}

			if (container === document && translationApplied) {
				this.updateSelector(targetLang);
			}
		} finally {
			if (needsOverlay) {
				this.hideDeeplTranslatingOverlay();
			}
		}

		rsBtn.forEach((btn) => btn.classList.remove('opacity-50', 'pointer-events-none'));
	}

	async updateSelector(targetLang) {
		document.documentElement.lang = targetLang.toLowerCase();

		const languageSelector = document.querySelectorAll('[data-deepl-translate]');
		const languageReset = document.querySelectorAll('[data-deepl-reset]');

		languageSelector.forEach((selector) => {
			this.updateSelectState(selector, targetLang);
		});
		languageReset.forEach((resetbtn) => resetbtn.classList.remove('hidden'));
		this.updateCurrentLanguageDisplay(targetLang);
	}

	// Used in backend to manually update target languages
	async updateLanguages() {
		let formData = new FormData();

		formData.append('action', 'update_deepl_languages');

		const request = new Request(strl_vars.ajax_url, {
			cache: 'no-store',
			method: 'POST',
			body: formData,
		});

		let results = await fetch(request)
			.then((response) => response.text())
			.then((response) => {
				response = JSON.parse(response);
				if (response.success) {
					alert('Languages updated successfully!');
				} else {
					alert(
						'An error occured during update, please try again or contact Stuurlui if this error persists.'
					);
					console.log(response);
				}
			})
			.catch((err) => {
				console.log(err);
				return false;
			});
	}
}
